import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { ATTACHMENT_SERVICE } from '@core/injection-tokens/attachments.injection.token';
import { EDITOR_SERVICE } from '@core/injection-tokens/editor.injection.token';
import { NOTES_SERVICE } from '@core/injection-tokens/notes.injection.token';
import { NoteService } from '@modules/notes/services/note.service';
import { EffectsModule } from '@ngrx/effects';
import {
  FullRouterStateSerializer,
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { META_REDUCERS, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EditorService } from '@shared/services/editor.service';
import { MojoAvatarModule } from 'mojo-avatar';
import { FEATURE_ACCESS_API_URL, MojoFeatureAccessModule } from 'mojo-feature-access';
import { FeatureFlagsModule } from 'mojo-feature-flags';
import { MojoIrpAttachmentsModule } from 'mojo-irp-attachments';
import { MojoPrivilegesService } from 'mojo-privileges';
import { QuillModule } from 'ngx-quill';

import { reducers } from '@app/app.reducer';
import { CollectionsModule } from '@app/collections';
import { AuthModule } from '@core/auth';
import { CanDeactivateGuard, InitialDataGuard } from '@core/guards';
import {
  SHARE_RESOURCE_AUTOCOMPLETE_SERVICE,
  SHARE_RESOURCE_SERVICE,
  SHARE_TEAM_MEMBER_SERVICE,
} from '@core/injection-tokens/share-resource.injection.token';
import { ParamsInterceptor } from '@core/interceptors/params.interceptor';
import { JobSharingAutocompleteService } from '@core/services/job-sharing-autocomplete.service';
import { SettingsModule } from '@core/settings/settings.module';
import { environment } from '@env/environment';
import { NotesModule } from '@modules/notes/notes.module';
import { NotificationsModule } from '@modules/notifications/notifications.module';
import { AttachmentService, StatusImageTypes } from '@shared';
import { NavigationHistoryModule } from '@shared/navigation-history/navigation-history.module';
import { CustomRouterStateSerializer } from '@shared/router-state-serializer';
import { SharedModule } from '@shared/shared.module';

import { PrivilegesAdapter } from './adapters/privileges.adapter';
import { CypressModule } from './cypress/cypress.module';
import {
  AVATAR_CONFIG_SERVICE,
  AVATAR_DOWNLOAD_SERVICE,
} from './injection-tokens/avatar.injection.token';
import { TenantInterceptor } from './interceptors/tenant.interceptor';
import { ModalsModule } from './modals/modals.module';
import { WebsocketModule } from './websocket/websocket.module';
import { SharedResourcesService } from '@core/shared-resources/services/shared-resources.service';
import { SharedResourcesModule } from '@core/shared-resources/shared-resources.module';
import { SharedResourcesAutocompleteService } from '@core/shared-resources/services/shared-resources-autocomplete.service';
import { TALENT_POOLS_SERVICE } from '@core/injection-tokens/talent-pools.injection.token';
import { TalentPoolsService } from '@shared/services/talent-pools.service';
import { TASKS_SERVICE } from '@core/injection-tokens/tasks.injection.token';
import { TasksService } from '@shared/services/tasks.service';
import { SharedResourcesApiService } from './shared-resources/services/shared-resources-api.service';
import {
  MojoActivityTrackingMetaReducerFactory,
  mojoActivityTrackingMetaReducerFactory,
  MojoActivityTrackingModule,
} from 'mojo-activity-tracking';
import { activityTrackingConfig } from '@core/activity-tracking/activity-tracking.config';

@NgModule({
  exports: [SharedModule, SettingsModule],
  imports: [
    HttpClientModule,
    SharedModule.forRoot(),
    NotesModule,
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    StoreRouterConnectingModule.forRoot({
      serializer: FullRouterStateSerializer,
    }),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      name: 'Fastr.ai',
      features: { test: true },
      logOnly: environment.production,
    }),
    ModalsModule,
    AuthModule.forRoot(),
    CollectionsModule,
    NavigationHistoryModule,
    SettingsModule,
    FeatureFlagsModule.forRoot(environment.API_BASE_URL),
    MojoFeatureAccessModule,
    WebsocketModule,
    NotificationsModule.forRoot(),
    CypressModule.forRoot(),
    QuillModule.forRoot(),
    MojoAvatarModule.forRoot(AVATAR_DOWNLOAD_SERVICE, AVATAR_CONFIG_SERVICE),
    SharedResourcesModule,
    MojoIrpAttachmentsModule.forRoot(ATTACHMENT_SERVICE),
    MojoActivityTrackingModule.forRoot(activityTrackingConfig),
  ],
  providers: [
    CanDeactivateGuard,
    InitialDataGuard,
    MojoPrivilegesService,
    SharedResourcesAutocompleteService,
    { provide: HTTP_INTERCEPTORS, useClass: ParamsInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TenantInterceptor, multi: true },
    { provide: FEATURE_ACCESS_API_URL, useValue: environment.API_BASE_URL + '/license' },
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    {
      provide: SHARE_RESOURCE_SERVICE,
      useClass: SharedResourcesService,
    },
    {
      provide: SHARE_TEAM_MEMBER_SERVICE,
      useClass: SharedResourcesApiService,
    },
    {
      provide: SHARE_RESOURCE_AUTOCOMPLETE_SERVICE,
      useClass: JobSharingAutocompleteService,
    },
    {
      provide: TALENT_POOLS_SERVICE,
      useClass: TalentPoolsService,
    },
    {
      provide: TASKS_SERVICE,
      useClass: TasksService,
    },
    {
      provide: NOTES_SERVICE,
      useClass: NoteService,
    },
    {
      provide: EDITOR_SERVICE,
      useClass: EditorService,
    },
    {
      provide: AVATAR_DOWNLOAD_SERVICE,
      useClass: AttachmentService,
    },
    {
      provide: ATTACHMENT_SERVICE,
      useClass: AttachmentService,
    },
    {
      provide: AVATAR_CONFIG_SERVICE,
      useValue: {
        ribbonTypes: StatusImageTypes,
        crownPath: 'assets/images/mojo-crown.png',
      },
    },
    {
      provide: META_REDUCERS,
      useFactory: mojoActivityTrackingMetaReducerFactory,
      deps: [MojoActivityTrackingMetaReducerFactory],
      multi: true,
    },
  ],
})
export class CoreModule {
  constructor(
    @Optional() @SkipSelf() parentModule: CoreModule,
    private privilegesAdapter: PrivilegesAdapter,
  ) {
    if (parentModule) {
      throw new Error('CoreModule has already been loaded. Import it once, in the AppModule only.');
    }
    privilegesAdapter.initializeErrorHandling();
  }
}
